package frontend.modals

import AccountSelect
import TransferTypeSelect
import androidx.compose.runtime.*
import net.sergeych.ugdx.shared.ApiException
import net.sergeych.ugdx.shared.Order
import net.sergeych.ugdx.shared.TransferOrder
import net.sergeych.ugdx.shared.api.ApiAccount
import com.ionspin.kotlin.bignum.decimal.toBigDecimal
import components.*
import frontend.App
import frontend.components.Modal
import frontend.lib.classList
import frontend.lib.displayCurrencyCode
import frontend.models.User
import frontend.pages.TransferType
import frontend.pages.Wallet
import frontend.pages.getAccountName
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.name
import org.jetbrains.compose.web.dom.*

@Composable
fun TransferModal(user: User, fromAccount: ApiAccount, onClose: () -> Unit) {
    val scope = rememberCoroutineScope()
    var error: String? by mutableStateOf(null)
    var transferType: TransferType by remember { mutableStateOf(TransferType.LOGIN) }
    var toLogin: String by remember { mutableStateOf("") }
    var toAccount: ApiAccount? by remember { mutableStateOf(null) }
    var amount by remember { mutableStateOf("") }
    var purpose by remember { mutableStateOf("") }
    var order: Order? by remember { mutableStateOf(null) }
    var agreed: Boolean by remember { mutableStateOf(false) }

    fun transferLogin(toLogin: String, amountString: String, purpose: String) {
        if (amountString == "") { error = "Amount can't be empty"; return }

        try {
            val amount = amountString.toBigDecimal()
            if (toLogin == "") { error = "Enter recipient login"; return }

            GlobalScope.launch {
                try {
                    order = user.createTransferOrder(fromAccount, amount, toLogin, purpose)
                    error = null
                } catch(e: ApiException) {
                    error = e.message
                }
            }
        } catch(e: ArithmeticException) {
            error = "Amount is not a valid number"
        }
    }

    fun transferLocal(toAccount: ApiAccount?, amountString: String) {
        if (amountString == "") { error = "Amount can't be empty"; return }

        try {
            val amount = amountString.toBigDecimal()
            if (toAccount == null) { error = "Select destination account"; return }

            GlobalScope.launch {
                try {
                    user.transferLocal(fromAccount, toAccount, amount)
                    error = null
                    onClose()
                } catch(e: ApiException) {
                    error = e.message
                }
            }
        } catch(e: ArithmeticException) {
            error = "Amount is not a valid number"
        }
    }

    fun submitOrder(order: Order) {
        GlobalScope.launch {
            order.submit()
            onClose()
        }
    }

    @Composable
    fun displayPosition(label: String, value: String) {
        Div(attrs = { classList("py-4 sm:py-3 sm:grid sm:grid-cols-3 sm:gap-4") }) {
            Dt(attrs = { classList("text-sm font-medium text-gray-500") }) {
                Text(label)
            }
            Dd(attrs = { classList("mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2") }) {
                Text(value)
            }
        }
    }

    Modal(onClose = { onClose() }) {
        if (order != null) {
            order?.let {
                val tOrder = it as TransferOrder
                val payer = getAccountName(null, tOrder.payerAccountId, user.accounts)

                Div(attrs = { classList("pb-3") }) {
                    H3(attrs = { classList("text-lg leading-6 font-medium text-gray-900") }) {
                        Text("Order preview")
                    }
                    P(attrs = { classList("mt-1 max-w-2xl text-sm text-gray-500") }) {
                        Text("Please, review order details before confirmation")
                    }
                }
                Div(attrs = { classList("border-t border-gray-200 px-4 pt-3 sm:p-0") }) {
                    Dl(attrs = { classList("border-t border-gray-200 px-4 py-5 sm:p-0") }) {
                        displayPosition("Pay from", payer)
                        displayPosition("Amount", "${tOrder.amount.toPlainString()} ${displayCurrencyCode(fromAccount.currencyCode)}")
                        displayPosition("Purpose", tOrder.purpose)
                    }
                }
                Div(attrs = { classList("relative flex items-start") }) {
                    Div(attrs = { classList("flex items-center h-5") }) {
                        Input(InputType.Checkbox) {
                            id("agreement")
                            attr("aria-describedby", "agreement-description")
                            name("agreement")
                            classList("focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded")
                            onChange { agreed = true }
                        }
                    }

                    Div(attrs = { classList("ml-3 text-sm") }) {
                        Label(forId = "agreement", attrs = {
                            classList("font-medium text-gray-700")
                        }) { Text("I understand") }
                        Span(attrs = {
                            id("agreement-description")
                            classList("text-gray-500")
                        }) {
                            Span(attrs = { classList("sr-only") }) { Text("I understand") }
                            Text(" that order can't be reverted and want to proceed")
                        }
                    }
                }
//                <div class="relative flex items-start">
//                <div class="flex items-center h-5">
//                <input id="comments" aria-describedby="comments-description" name="comments" type="checkbox" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded">
//                </div>
//                <div class="ml-3 text-sm">
//                <label for="comments" class="font-medium text-gray-700">New comments</label>
//                <span id="comments-description" class="text-gray-500"><span class="sr-only">New comments </span>so you always know what's happening.</span>
//                </div>
//                </div>

                if (agreed) Div(attrs = { classList("pt-3") }) {
                    TButton(attrs = {
                        onClick { submitOrder(it) }
                    }) {
                        Text("Confirm")
                    }
                }
            }
        }
        else TForm {
            H3(attrs = { classList("text-lg leading-6 font-medium text-gray-900") }) {
                Text("Create ${displayCurrencyCode(fromAccount.currencyCode)} transfer from ${fromAccount.name ?: fromAccount.id.toString()}")
            }

            TransferTypeSelect("ttype", transferType) {
                onChange { it?.let { transferType = it } }
                label { Text("Select transfer type") }
            }

            if (transferType == TransferType.LOGIN) {
                TTextInput("login", toLogin) {
                    onInput { toLogin = it }
                    label { Text("Recipient login") }
                }

                TTextInput("purpose", purpose) {
                    onInput { purpose = it }
                    label { Text("Transfer purpose") }
                }
            }

            if (transferType == TransferType.LOCAL) {
                val accounts = user.accounts
                val filtered = accounts.filter {
                    it.currencyCode == fromAccount.currencyCode && it.id != fromAccount.id
                }

                //
                if (filtered.isNotEmpty()) {
                    if (toAccount == null) toAccount = filtered.first()

                    AccountSelect("recipientAccount", toAccount) {
                        label { Text("Select destination account") }
                        options(filtered)
                        onChange { it?.let { toAccount = it } }
                    }
                } else {
                    Text("You have no other ${displayCurrencyCode(fromAccount.currencyCode)} accounts")
                }
            }

            TTextInput("amount", amount) {
                numeric()
                onInput { amount = it }
                label { Text("amount") }
            }

            if (error !== null) P(attrs = { classes("text-red-600") }) {
                Text(error ?: "")
            }

            TButton(attrs = {
                onClick {
                    if (transferType == TransferType.LOCAL) transferLocal(toAccount, amount)
                    if (transferType == TransferType.LOGIN) transferLogin(toLogin, amount, purpose)
                }
            }) {
                Text("Create transfer")
            }
        }
    }
}
